import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../../config';
import useTextCheck from './useTextCheck';
import TextCheckNotice from './TextCheckNotice';
import './TextCandidateModal.css';

// Quick text pop-up opened from the chat button next to the call button on Driver Candidates and
// Candidate Archive (October 2026). Shows the candidate's recent texts and sends through the same
// Messages Inbox endpoint, so everything lands in /messages too. Texts go out from the candidate
// owner's RingCentral number (the same rule as the Messages Inbox).
const SHOW_LAST = 6;

function TextCandidateModal({ candidate, onClose }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [clarifyQ, setClarifyQ] = useState(null);
  const { unclear: draftUnclear } = useTextCheck(draft);
  const listRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sms-inbox/thread/${candidate.id}`, { headers });
      setMessages(data.messages || []);
      setInfo(data.candidate);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load messages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [candidate.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  // Server corrects the English before sending; an unclear text comes back 409 and isn't sent
  const send = async (sendAsWritten = false) => {
    if (!draft.trim() || sending) return;
    setSending(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/sms-inbox/thread/${candidate.id}/send`, { message: draft, sendAsWritten }, { headers });
      setDraft('');
      setClarifyQ(null);
      await load();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.code === 'needs_clarification') setClarifyQ(err.response.data.question);
      else setError(err.response?.data?.error || 'Failed to send text.');
    } finally {
      setSending(false);
    }
  };

  const recent = messages.slice(-SHOW_LAST);
  const notConnected = info && !info.recruiter_connected;
  const fmt = ts => new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div className="tcm-overlay" onClick={onClose}>
      <div className="tcm-modal" onClick={e => e.stopPropagation()}>
        <div className="tcm-header">
          <div>
            <div className="tcm-title">Text {candidate.candidate_name}</div>
            <div className="tcm-sub">{candidate.phone}{info?.recruiter_name ? ` · sends from ${info.recruiter_name}'s number` : ''}</div>
          </div>
          <button className="tcm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="tcm-list" ref={listRef}>
          {loading ? (
            <div className="tcm-empty">Loading…</div>
          ) : recent.length === 0 ? (
            <div className="tcm-empty">No texts yet.</div>
          ) : (
            <>
              {messages.length > SHOW_LAST && <div className="tcm-more">{messages.length - SHOW_LAST} earlier messages in Messages</div>}
              {recent.map(m => (
                <div key={m.id} className={`tcm-row ${m.direction === 'outbound' ? 'out' : 'in'}`}>
                  <div className={`tcm-bubble${m.send_failed ? ' failed' : ''}`}>{m.body}</div>
                  <div className="tcm-meta">{m.auto_sent ? 'Auto-sent · ' : ''}{m.original_body ? <><span className="tcn-corrected" title={`You typed: ${m.original_body}`}>✓ Corrected</span> · </> : null}{fmt(m.created_at)}</div>
                </div>
              ))}
            </>
          )}
        </div>

        {error && <div className="tcm-error">{error}</div>}
        {notConnected && <div className="tcm-error">{info.recruiter_name || 'The candidate owner'} hasn't connected RingCentral, so texts can't be sent yet.</div>}

        <div className="tcm-compose">
          <TextCheckNotice question={clarifyQ || draftUnclear} sending={sending} onSendAsWritten={() => send(true)} />
          <textarea
            value={draft}
            onChange={e => { setDraft(e.target.value); setClarifyQ(null); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a text… (Enter to send, Shift+Enter for a new line)"
            rows={3}
            disabled={notConnected}
          />
          <div className="tcm-actions">
            <button type="button" className="tcm-link" onClick={() => navigate(`/messages?candidateId=${candidate.id}`)}>Open in Messages</button>
            <button type="button" className="tcm-send" onClick={() => send()} disabled={sending || !draft.trim() || notConnected}>
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TextCandidateModal;
